import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  Fade,
  InputAdornment,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  RemoveCircleOutline as RemoveIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ClasseService from '../../services/ClasseService';
import UserService from '../../services/UserService';
import MainContainer from '../../components/MainContainer';
import { headerBanqueCV } from '../../data/modules/banque-cv-header';
import DirectionService from '../../services/DirectionService';
import PlanningService from '../../services/PlanningService';

const Classes = () => {
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({ 
    nom: '', 
    date_active: ''
  });
  const [formErrors, setFormErrors] = useState({ nom: false });
  const [feedbackDialog, setFeedbackDialog] = useState({
    open: false,
    type: 'success',
    title: '',
    message: '',
    onConfirm: null
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [saveLoading, setSaveLoading] = useState(false);

  // États pour la gestion des utilisateurs de la classe
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classUsers, setClassUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [allAvailableUsers, setAllAvailableUsers] = useState([]);
  const [searchUserTerm, setSearchUserTerm] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // États pour les filtres de direction et service
  const [directions, setDirections] = useState([]);
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({
    direction_id: 'all',
    service_id: '0',
    type: 'all'
  });

  // Nouvel état pour regrouper les utilisateurs par fonction
  const [groupedAvailableUsers, setGroupedAvailableUsers] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  // Chargement des directions au montage
  useEffect(() => {
    loadDirections();
    loadClasses();
  }, []);

  // Chargement des services quand une direction spécifique est sélectionnée
  useEffect(() => {
    if (filters.direction_id && filters.direction_id !== 'all') {
      loadServices(filters.direction_id);
    } else {
      setServices([]);
    }
  }, [filters.direction_id]);

  // Chargement des utilisateurs quand les filtres changent
  useEffect(() => {
    loadUsersByFilters();
  }, [filters.direction_id, filters.service_id, filters.type]);

  // Regrouper les utilisateurs par fonction quand availableUsers change
  useEffect(() => {
    groupUsersByFilteredUsers();
  }, [allAvailableUsers, searchUserTerm, classUsers]);

  const loadDirections = async () => {
    try {
      const result = await DirectionService.getDirection();
      if (result && result.status === 1) {
        const directionsList = Array.isArray(result.data) ? result.data : [];
        setDirections(directionsList);
      }
    } catch (error) {
    }
  };

  const loadServices = async (directionId) => {
    try {
      const result = await PlanningService.Direction(directionId);
      if (result && result.data) {
        setServices(Array.isArray(result.data) ? result.data : []);
      } else {
        setServices([]);
      }
    } catch (error) {
      setServices([]);
    }
  };

  const loadUsersByFilters = async () => {
    setUsersLoading(true);
    try {
      let result;
      
      if (filters.direction_id === 'all') {
        // Charger tous les utilisateurs toutes directions confondues
        result = await UserService.getAllUserlist('', '', filters.type);
      } else {
        const directionId = filters.direction_id;
        const serviceId = filters.service_id === '0' ? '' : filters.service_id;
        result = await UserService.getAllUserlist(directionId, serviceId, filters.type);
      }
      
      if (result && result.data) {
        const formattedUsers = result.data.map((row) => ({ 
          ...row, 
          id: row.user_id,
          nom_complet: `${row.first_name || ''} ${row.last_name || ''}`.trim(),
          fonction: row.function_name || 'Sans fonction'
        }));
        setAllAvailableUsers(formattedUsers);
        setUsers(formattedUsers);
      } else {
        setAllAvailableUsers([]);
        setUsers([]);
      }
    } catch (error) {
      setAllAvailableUsers([]);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadClasses = async () => {
    setLoading(true);
    try {
      const result = await ClasseService.getAllClasses();
      if (result && result.status === true) {
        setClasses(Array.isArray(result.data) ? result.data : []);
      } else {
        setClasses([]);
      }
    } catch (error) {
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClassUsers = async (classeId) => {
    setUsersLoading(true);
    try {
      const result = await ClasseService.getClassUsers(classeId);
      if (result && result.status === true) {
        const formattedUsers = Array.isArray(result.data) ? result.data.map(user => ({ 
          ...user, 
          nom_complet: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
          fonction: user.function_name || 'Sans fonction'
        })) : [];
        setClassUsers(formattedUsers);
      } else {
        setClassUsers([]);
      }
    } catch (error) {
      setClassUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const openUsersDialog = async (classe) => {
    setSelectedClass(classe);
    setUsersDialogOpen(true);
    setActiveTab(0);
    setSearchUserTerm('');
    setSelectedUsers([]);
    setExpandedGroups({});
    setAllAvailableUsers([]);
    setClassUsers([]);
    
    // Réinitialiser les filtres avec 'all' pour voir tous les utilisateurs
    setFilters({
      direction_id: 'all',
      service_id: '0',
      type: 'all'
    });
    
    await loadClassUsers(classe.id);
  };

  // Mettre à jour la liste des utilisateurs disponibles
  useEffect(() => {
    if (allAvailableUsers.length > 0 && classUsers.length >= 0) {
      const classUserIds = classUsers.map(u => u.id);
      const available = allAvailableUsers.filter(user => !classUserIds.includes(user.id));
      setAvailableUsers(available);
    } else if (allAvailableUsers.length === 0) {
      setAvailableUsers([]);
    }
  }, [allAvailableUsers, classUsers]);

  // Fonction pour regrouper les utilisateurs par fonction avec recherche
  const groupUsersByFilteredUsers = () => {
    const filtered = availableUsers.filter(user =>
      user.nom_complet?.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
      user.fonction?.toLowerCase().includes(searchUserTerm.toLowerCase())
    );

    const grouped = filtered.reduce((acc, user) => {
      const fonction = user.fonction || 'Sans fonction';
      if (!acc[fonction]) {
        acc[fonction] = [];
      }
      acc[fonction].push(user);
      return acc;
    }, {});

    // Trier les fonctions par ordre alphabétique
    const sortedGrouped = {};
    Object.keys(grouped).sort().forEach(key => {
      sortedGrouped[key] = grouped[key];
    });

    setGroupedAvailableUsers(sortedGrouped);
  };

  const handleToggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleToggleGroupSelection = (fonction) => {
    const groupUserIds = groupedAvailableUsers[fonction].map(user => user.id);
    const allSelected = groupUserIds.every(id => selectedUsers.includes(id));
    
    if (allSelected) {
      setSelectedUsers(prev => prev.filter(id => !groupUserIds.includes(id)));
    } else {
      setSelectedUsers(prev => [...prev, ...groupUserIds.filter(id => !prev.includes(id))]);
    }
  };

  const handleSelectAllUsers = () => {
    const allUserIds = Object.values(groupedAvailableUsers).flat().map(user => user.id);
    if (selectedUsers.length === allUserIds.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(allUserIds);
    }
  };

  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) {
      showFeedback('warning', 'Attention', 'Veuillez sélectionner au moins un utilisateur');
      return;
    }

    setUsersLoading(true);
    try {
      const result = await ClasseService.addUsersToClass(selectedClass.id, selectedUsers);
      if (result && result.status === true) {
        showFeedback('success', 'Succès', `${selectedUsers.length} utilisateur(s) ajouté(s) avec succès`);
        await loadClassUsers(selectedClass.id);
        setSelectedUsers([]);
        await loadUsersByFilters();
      } else {
        showFeedback('error', 'Erreur', result?.message || 'Erreur lors de l\'ajout des utilisateurs');
      }
    } catch (error) {
      showFeedback('error', 'Erreur', error?.message || 'Erreur lors de l\'ajout des utilisateurs');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    setConfirmDialog({
      open: true,
      title: 'Confirmation de retrait',
      message: `Êtes-vous sûr de vouloir retirer "${userName}" de cette classe ?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setUsersLoading(true);
        try {
          const result = await ClasseService.removeUserFromClass(selectedClass.id, userId);
          if (result && result.status === true) {
            showFeedback('success', 'Succès', 'Utilisateur retiré avec succès');
            await loadClassUsers(selectedClass.id);
            await loadUsersByFilters();
          } else {
            showFeedback('error', 'Erreur', result?.message || 'Erreur lors du retrait de l\'utilisateur');
          }
        } catch (error) {
          showFeedback('error', 'Erreur', error?.message || 'Erreur lors du retrait de l\'utilisateur');
        } finally {
          setUsersLoading(false);
        }
      }
    });
  };

  const refreshUsers = () => {
    loadUsersByFilters();
  };

  const showFeedback = (type, title, message, onConfirm = null) => {
    setFeedbackDialog({
      open: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeFeedback = () => {
    setFeedbackDialog({ ...feedbackDialog, open: false });
    if (feedbackDialog.onConfirm) {
      feedbackDialog.onConfirm();
    }
  };

  const validateForm = () => {
    const errors = { nom: !formData.nom.trim() };
    setFormErrors(errors);
    return !errors.nom;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showFeedback('error', 'Erreur de validation', 'Le nom de la classe est requis');
      return;
    }

    setSaveLoading(true);
    try {
      let result;
      if (editingClass) {
        result = await ClasseService.updateClasse(editingClass.id, formData);
      } else {
        result = await ClasseService.createClasse(formData);
      }

      if (result && result.status === true) {
        showFeedback('success', 'Succès', result.message || 'Opération réussie', () => {
          setModalOpen(false);
          setEditingClass(null);
          setFormData({ nom: '', date_active: '' });
          setFormErrors({ nom: false });
          loadClasses();
        });
      } else {
        showFeedback('error', 'Erreur', result?.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      showFeedback('error', 'Erreur', error?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id, nom) => {
    setConfirmDialog({
      open: true,
      title: 'Confirmation de suppression',
      message: `Êtes-vous sûr de vouloir supprimer la classe "${nom}" ? Cette action est irréversible.`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        setSaveLoading(true);
        try {
          const result = await ClasseService.deleteClasse(id);
          if (result && result.status === true) {
            showFeedback('success', 'Succès', result.message || 'Classe supprimée avec succès', () => {
              loadClasses();
            });
          } else {
            showFeedback('error', 'Erreur', result?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          showFeedback('error', 'Erreur', error?.message || 'Erreur lors de la suppression');
        } finally {
          setSaveLoading(false);
        }
      }
    });
  };

  const openModalForEdit = (classe) => {
    if (classe) {
      setEditingClass(classe);
      setFormData({
        nom: classe.nom || '',
        date_active: classe.date_active || ''
      });
      setFormErrors({ nom: false });
      setModalOpen(true);
    }
  };

  const openModalForCreate = () => {
    setEditingClass(null);
    setFormData({ nom: '', date_active: '' });
    setFormErrors({ nom: false });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!saveLoading) {
      setModalOpen(false);
      setEditingClass(null);
      setFormData({ nom: '', date_active: '' });
      setFormErrors({ nom: false });
    }
  };

  const filteredClasses = Array.isArray(classes) 
    ? classes.filter(c => c && c.nom && c.nom.toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  const totalAvailableCount = Object.values(groupedAvailableUsers).flat().length;
  const totalSelectedCount = selectedUsers.length;

  const getFeedbackIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 60, color: '#f44336' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 60, color: '#ff9800' }} />;
      default:
        return <SchoolIcon sx={{ fontSize: 60, color: '#2196f3' }} />;
    }
  };

  const getFeedbackColor = (type) => {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      default:
        return '#2196f3';
    }
  };
    
  return (
    <MainContainer {...headerBanqueCV}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Gestion des Classes
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openModalForCreate}
            disabled={saveLoading}
            color='primary'
          >
            Nouvelle classe
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Rechercher une classe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Typography 
            variant="caption" 
            color="textSecondary" 
            sx={{ mt: 0.5, display: 'block', ml: 1 }}
          >
            <SearchIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
            Veuillez taper le nom de la classe que vous recherchez 
          </Typography>
        </Box>

        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <Chip 
            label={`Total: ${filteredClasses.length}`} 
            color="primary" 
            variant="outlined"
          />
          {searchTerm && (
            <Chip 
              label={`Résultats pour: "${searchTerm}"`} 
              color="secondary" 
              variant="outlined"
              onDelete={() => setSearchTerm('')}
            />
          )}
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>N°</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nom de la classe</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date d'activation</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 1 }}>Chargement...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredClasses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      {searchTerm ? (
                        <>
                          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                          <Typography color="textSecondary">
                            Aucune classe ne correspond à "{searchTerm}"
                          </Typography>
                          <Button onClick={() => setSearchTerm('')}>
                            Effacer la recherche
                          </Button>
                        </>
                      ) : (
                        <Typography color="textSecondary">
                          Aucune classe trouvée. Cliquez sur "Nouvelle classe" pour en créer une.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClasses.map((classe, index) => (
                  <TableRow key={classe.id || index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {classe.nom || 'Sans nom'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {classe.date_active ? new Date(classe.date_active).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => openModalForEdit(classe)}
                        size="small"
                        disabled={saveLoading}
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(classe.id, classe.nom)}
                        size="small"
                        disabled={saveLoading}
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => openUsersDialog(classe)}
                        size="small"
                        disabled={saveLoading}
                        title="Gérer les utilisateurs"
                      >
                        <PeopleIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog pour la gestion des utilisateurs */}
        <Dialog
          open={usersDialogOpen}
          onClose={() => !usersLoading && setUsersDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" />
                <Typography variant="h6">
                  Gestion des utilisateurs - {selectedClass?.nom}
                </Typography>
              </Box>
              <IconButton onClick={() => setUsersDialogOpen(false)} disabled={usersLoading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              <Tab label={`Membres (${classUsers.length})`} />
              <Tab label="Ajouter des utilisateurs" />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                {usersLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : classUsers.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <Typography color="textSecondary">
                      Aucun utilisateur dans cette classe
                    </Typography>
                    <Button
                      startIcon={<PersonAddIcon />}
                      onClick={() => setActiveTab(1)}
                      sx={{ mt: 2 }}
                    >
                      Ajouter des utilisateurs
                    </Button>
                  </Box>
                ) : (
                  <List>
                    {classUsers.map((user, index) => (
                      <React.Fragment key={user.id}>
                        <ListItem>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                            {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                          </Avatar>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {user.first_name} {user.last_name}
                                </Typography>
                                <Typography variant="caption" color="primary">
                                  {user.fonction}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography component="span" variant="body2" color="textSecondary">
                                  {user.email}
                                </Typography>
                                {user.direction && (
                                  <Chip 
                                    label={user.direction} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                {user.service && (
                                  <Chip 
                                    label={user.service} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleRemoveUser(user.id, `${user.first_name} ${user.last_name}`)}
                              disabled={usersLoading}
                              title="Retirer de la classe"
                            >
                              <RemoveIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < classUsers.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                {/* Filtres Direction et Service */}
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FilterIcon color="primary" />
                      <Typography variant="subtitle2" fontWeight="bold">
                        Filtrer les utilisateurs
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={refreshUsers} title="Actualiser">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Direction</InputLabel>
                        <Select
                          name="direction_id"
                          value={filters.direction_id}
                          onChange={(e) => {
                            setFilters({ 
                              ...filters, 
                              direction_id: e.target.value, 
                              service_id: '0'
                            });
                            setSelectedUsers([]); 
                            setExpandedGroups({});
                          }}
                          label="Direction"
                        >
                          <MenuItem value="all">Toutes les directions</MenuItem>
                          {directions.map((dir) => (
                            <MenuItem key={dir.direction_id} value={dir.direction_id}>
                              {dir.name} ({dir.type})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth size="small" disabled={filters.direction_id === 'all'}>
                        <InputLabel>Service</InputLabel>
                        <Select
                          name="service_id"
                          value={filters.service_id}
                          onChange={(e) => {
                            setFilters({ ...filters, service_id: e.target.value });
                            setSelectedUsers([]); 
                            setExpandedGroups({});
                          }}
                          label="Service"
                        >
                          <MenuItem value="0">Tous les services</MenuItem>
                          {services.map((serv) => (
                            <MenuItem key={serv.service_id} value={serv.service_id}>
                              {serv.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={filters.type}
                          onChange={(e) => {
                            setFilters({ ...filters, type: e.target.value });
                            setSelectedUsers([]); 
                            setExpandedGroups({});
                          }}
                          label="Type"
                        >
                          <MenuItem value="all">Tous</MenuItem>
                          <MenuItem value="GRD">GRD</MenuItem>
                          <MenuItem value="nonGRD">Non GRD</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip 
                      label={`${totalAvailableCount} utilisateur(s) disponible(s)`} 
                      color="primary" 
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Paper>

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Rechercher un utilisateur par nom, prénom, email ou fonction..."
                  value={searchUserTerm}
                  onChange={(e) => setSearchUserTerm(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchUserTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchUserTerm('')}
                          edge="end"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                {usersLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : Object.keys(groupedAvailableUsers).length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <Typography color="textSecondary">
                      {searchUserTerm ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur disponible'}
                    </Typography>
                    {!searchUserTerm && (
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={refreshUsers}
                        sx={{ mt: 2 }}
                      >
                        Actualiser la liste
                      </Button>
                    )}
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Button
                        size="small"
                        onClick={handleSelectAllUsers}
                        startIcon={totalSelectedCount === totalAvailableCount && totalAvailableCount > 0 ? <CheckCircleIcon /> : <Checkbox />}
                      >
                        {totalSelectedCount === totalAvailableCount && totalAvailableCount > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </Button>
                      <Typography variant="caption" color="textSecondary">
                        {totalAvailableCount} utilisateur(s) au total
                      </Typography>
                    </Box>

                    <List sx={{ maxHeight: 450, overflow: 'auto' }}>
                      {Object.entries(groupedAvailableUsers).map(([fonction, usersList]) => {
                        const groupUserIds = usersList.map(u => u.id);
                        const allSelectedInGroup = groupUserIds.every(id => selectedUsers.includes(id));
                        const someSelectedInGroup = groupUserIds.some(id => selectedUsers.includes(id));
                        const isExpanded = expandedGroups[fonction] !== false;

                        return (
                          <React.Fragment key={fonction}>
                            <Accordion 
                              expanded={isExpanded}
                              onChange={() => setExpandedGroups(prev => ({ ...prev, [fonction]: !prev[fonction] }))}
                              sx={{ 
                                boxShadow: 'none', 
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px !important',
                                mb: 1,
                                '&:before': { display: 'none' }
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  backgroundColor: '#f9f9f9',
                                  borderRadius: '8px',
                                  '&:hover': { backgroundColor: '#f0f0f0' }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                  <Checkbox
                                    checked={allSelectedInGroup}
                                    indeterminate={someSelectedInGroup && !allSelectedInGroup}
                                    onChange={() => handleToggleGroupSelection(fonction)}
                                    onClick={(e) => e.stopPropagation()}
                                    color="primary"
                                  />
                                  <GroupIcon color="action" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {fonction}
                                  </Typography>
                                  <Badge 
                                    badgeContent={usersList.length} 
                                    color="primary"
                                    sx={{ ml: 1 }}
                                  />
                                  <Box sx={{ flexGrow: 1 }} />
                                  <Typography variant="caption" color="textSecondary">
                                    {usersList.filter(u => selectedUsers.includes(u.id)).length} sélectionné(s)
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 0 }}>
                                <List disablePadding>
                                  {usersList.map((user, index) => (
                                    <React.Fragment key={user.id}>
                                      <ListItem
                                        button
                                        onClick={() => handleToggleUserSelection(user.id)}
                                        selected={selectedUsers.includes(user.id)}
                                        sx={{
                                          pl: 8,
                                          backgroundColor: selectedUsers.includes(user.id) ? '#E3F2FD' : 'transparent',
                                          '&:hover': {
                                            backgroundColor: selectedUsers.includes(user.id) ? '#BBDEFB' : '#F5F5F5',
                                          },
                                          '&.Mui-selected': {
                                            backgroundColor: '#E3F2FD',
                                            '&:hover': {
                                              backgroundColor: '#BBDEFB',
                                            }
                                          }
                                        }}
                                      >
                                        <Checkbox
                                          checked={selectedUsers.includes(user.id)}
                                          onChange={() => handleToggleUserSelection(user.id)}
                                          onClick={(e) => e.stopPropagation()}
                                          color="primary"
                                        />
                                        <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                                          {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                                        </Avatar>
                                        <ListItemText
                                          primary={
                                            <Box>
                                              <Typography variant="body1" fontWeight="500">
                                                {user.first_name} {user.last_name}
                                              </Typography>
                                              <Typography variant="caption" color="textSecondary">
                                                {user.email}
                                              </Typography>
                                            </Box>
                                          }
                                          secondary={
                                            <Box sx={{ mt: 0.5 }}>
                                              {user.direction && (
                                                <Chip 
                                                  label={user.direction} 
                                                  size="small" 
                                                  variant="outlined" 
                                                  sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                              )}
                                              {user.service && (
                                                <Chip 
                                                  label={user.service} 
                                                  size="small" 
                                                  variant="outlined" 
                                                  sx={{ mb: 0.5 }}
                                                />
                                              )}
                                            </Box>
                                          }
                                        />
                                      </ListItem>
                                      {index < usersList.length - 1 && <Divider sx={{ ml: 8 }} />}
                                    </React.Fragment>
                                  ))}
                                </List>
                              </AccordionDetails>
                            </Accordion>
                          </React.Fragment>
                        );
                      })}
                    </List>

                    {totalSelectedCount > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#E3F2FD', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {totalSelectedCount} utilisateur(s) sélectionné(s)
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setSelectedUsers([])}
                          disabled={usersLoading}
                        >
                          Tout désélectionner
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUsersDialogOpen(false)} disabled={usersLoading}>
              Fermer
            </Button>
            {activeTab === 1 && (
              <Button
                variant="contained"
                onClick={handleAddUsers}
                disabled={totalSelectedCount === 0 || usersLoading}
                startIcon={usersLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
              >
                {usersLoading ? 'Ajout...' : `Ajouter (${totalSelectedCount})`}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Modal Dialog pour création/édition */}
        <Dialog 
          open={modalOpen} 
          onClose={handleCloseModal} 
          maxWidth="sm" 
          fullWidth
          disableEscapeKeyDown={saveLoading}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon color="primary" />
              <Typography variant="h6">
                {editingClass ? 'Modifier la classe' : 'Nouvelle classe'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Nom de la classe *"
                value={formData.nom}
                onChange={(e) => {
                  setFormData({ ...formData, nom: e.target.value });
                  if (formErrors.nom && e.target.value.trim()) {
                    setFormErrors({ ...formErrors, nom: false });
                  }
                }}
                placeholder="Ex: L3 Informatique"
                fullWidth
                required
                error={formErrors.nom}
                helperText={formErrors.nom && "Le nom de la classe est requis"}
                disabled={saveLoading}
                autoFocus
              />
              <TextField
                label="Date d'activation"
                type="date"
                value={formData.date_active}
                onChange={(e) => setFormData({ ...formData, date_active: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={saveLoading}
                helperText="Date à partir de laquelle la classe sera active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseModal} 
              disabled={saveLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              color="primary"
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={20} /> : null}
            >
              {saveLoading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Feedback Dialog */}
        <Dialog
          open={feedbackDialog.open}
          onClose={closeFeedback}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Fade}
          transitionDuration={300}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={closeFeedback} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              {getFeedbackIcon(feedbackDialog.type)}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ color: getFeedbackColor(feedbackDialog.type), fontWeight: 'bold' }}
            >
              {feedbackDialog.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {feedbackDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={closeFeedback}
              variant="contained"
              sx={{
                backgroundColor: getFeedbackColor(feedbackDialog.type),
                '&:hover': {
                  backgroundColor: getFeedbackColor(feedbackDialog.type),
                  opacity: 0.9
                }
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmation pour suppression */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => !saveLoading && setConfirmDialog({ ...confirmDialog, open: false })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
              disabled={saveLoading}
            >
              Annuler
            </Button>
            <Button 
              onClick={confirmDialog.onConfirm} 
              variant="contained" 
              color="error"
              disabled={saveLoading}
            >
              {saveLoading ? 'Suppression...' : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Backdrop pour loading */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={saveLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    </MainContainer>
  );
};

export default Classes;